import { useState, useEffect } from 'react';
import { ModalHost } from './ModalHost';
import { StatusIcon } from '../common/StatusIcon';

interface RegistryStatus {
  weather_configured: boolean;
  llm_configured: boolean;
  database_configured: boolean;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const [status, setStatus] = useState<RegistryStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'api' | 'about'>('general');

  useEffect(() => {
    if (isOpen) {
      loadStatus();
    }
  }, [isOpen]);

  const loadStatus = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/registry/status');
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to load registry status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatusIndicator = (configured: boolean) => (
    <span className={`status-indicator ${configured ? 'status-indicator--ok' : 'status-indicator--warning'}`}>
      <StatusIcon status={configured ? 'ok' : 'warning'} />
    </span>
  );

  return (
    <ModalHost isOpen={isOpen} onClose={onClose} title="Settings" size="lg">
      <div className="settings-content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'general' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`tab ${activeTab === 'api' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('api')}
          >
            API Status
          </button>
          <button
            className={`tab ${activeTab === 'about' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
        </div>

        {activeTab === 'general' && (
          <div className="settings-section">
            <h4 className="text-sm font-semibold mb-4">Application Settings</h4>
            <p className="text-sm text-secondary">
              Configure application preferences and display options.
            </p>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="settings-section">
            <h4 className="text-sm font-semibold mb-4">API Configuration Status</h4>
            {isLoading ? (
              <div className="loading-spinner" />
            ) : status ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  {renderStatusIndicator(status.weather_configured)}
                  <span className="text-sm">Weather API (NWS)</span>
                </div>
                <div className="flex items-center gap-2">
                  {renderStatusIndicator(status.llm_configured)}
                  <span className="text-sm">LLM Provider</span>
                </div>
                <div className="flex items-center gap-2">
                  {renderStatusIndicator(status.database_configured)}
                  <span className="text-sm">Database</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-error">Failed to load configuration status.</p>
            )}
          </div>
        )}

        {activeTab === 'about' && (
          <div className="settings-section">
            <h4 className="text-sm font-semibold mb-4">Bridge Ice Risk & Mitigation</h4>
            <p className="text-sm text-secondary mb-2">
              Decision support for bridge icing prediction and mitigation.
            </p>
            <p className="text-xs text-secondary">
              Bossier Parish, Louisiana
            </p>
          </div>
        )}
      </div>
    </ModalHost>
  );
}
